import { useMemo } from 'react';
import { Bell, AlertOctagon, AlertTriangle, Info } from 'lucide-react';
import { timeAgo } from '../../lib/dates';
import type { Alert, AlertSeverity } from '../../types';
import { sectionError } from './useSectionQuery';
import type { SectionProps } from './useSectionQuery';
import { SectionShell } from './SectionShell';

const SEVERITY_RANK: Record<AlertSeverity, number> = { critical: 0, warning: 1, info: 2 };

/** The Worker hands them back newest-first; the dashboard wants the loudest first. */
function bySeverity(alerts: Alert[]): Alert[] {
    return [...alerts].sort((a, b) => {
        const r = SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity];
        return r !== 0 ? r : new Date(b.starts_at).getTime() - new Date(a.starts_at).getTime();
    });
}

function SeverityIcon({ severity }: { severity: AlertSeverity }) {
    if (severity === 'critical') return <AlertOctagon size={20} aria-hidden="true" />;
    if (severity === 'warning') return <AlertTriangle size={20} aria-hidden="true" />;
    return <Info size={20} aria-hidden="true" />;
}

/** Full-width red banner for critical alerts, rendered above the page header. */
export function CriticalBanner({ alerts }: { alerts: Alert[] }) {
    const critical = alerts.filter((a) => a.severity === 'critical');
    if (critical.length === 0) return null;
    return (
        <div className="critical-banner" role="alert">
            {critical.map((a) => (
                <div key={a.id} className="critical-banner-item">
                    <AlertOctagon size={22} aria-hidden="true" />
                    <div>
                        <strong>{a.title}</strong>
                        <p>{a.message}</p>
                    </div>
                </div>
            ))}
        </div>
    );
}

/**
 * Only there while an alert is running. No "all quiet" card and no skeleton:
 * an empty block on every visit is space the resident scrolls past. A failed
 * load still shows, because this is where an emergency alert is read (while
 * loading, `sectionError` is null and there are no alerts yet, so nothing).
 */
export function AlertsSection(props: SectionProps<Alert[]>) {
    const { data, onRetry } = props;
    const alerts = useMemo(() => bySeverity(data ?? []), [data]);
    const error = sectionError(props);
    if (!error && alerts.length === 0) return null;
    return (
        <SectionShell title="Alerts" icon={<Bell size={20} aria-hidden="true" />} error={error} onRetry={onRetry}>
            <div className="alerts-list">
                {alerts.map((a) => (
                    <div key={a.id} className={`alert-card alert-${a.severity}`}>
                        <span className="alert-icon"><SeverityIcon severity={a.severity} /></span>
                        <div className="alert-content">
                            <strong>{a.title}</strong>
                            <p>{a.message}</p>
                            <span className="alert-time">{timeAgo(a.starts_at)}</span>
                        </div>
                    </div>
                ))}
            </div>
        </SectionShell>
    );
}
